import { Copy, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  MINT,
  STONKFUN_URL,
  TELEGRAM_URL,
  X_URL,
  copyMint,
} from "@/lib/fronk";

const XMark = ({ className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
    fill="currentColor"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817-5.967 6.817H1.68l7.73-8.835L1.254 2.25h6.826l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);

const TelegramMark = ({ className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
    fill="currentColor"
  >
    <path d="M21.63 2.18a1.55 1.55 0 0 0-1.58-.11L2.8 9.12c-1.17.47-1.15 1.15-.21 1.44l4.42 1.38 1.7 5.28c.21.59.1.83.72.83.48 0 .69-.22.96-.48l2.13-2.07 4.43 3.27c.82.45 1.4.22 1.6-.76L21.5 4.08c.3-1.21-.16-1.71.13-1.9ZM8.54 11.62l8.64-5.45c.43-.26.82-.12.5.17l-7.13 6.44-.28 3.02-1.73-4.18Z" />
  </svg>
);

const BADGES = [
  "badge-pepe-coin",
  "badge-fee-capsule",
  "badge-shareholder-envelope",
  "badge-chart-eye",
  "badge-handshake",
  "badge-coin-umbrella",
  "badge-coffee-lips",
  "badge-fronk-cameo",
];

export const Footer = () => {
  return (
    <footer id="contact" className="relative overflow-hidden" data-testid="footer-section">
      <img
        src="/fronk/scenes/fronk-fee-rain-mailroom.webp"
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-[rgba(10,17,28,0.9)]" />

      <div className="relative z-10 mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <img
              src="/fronk/hero/fronk-wordmark-seal.webp"
              alt="FRONK seal wordmark"
              className="h-24 w-auto"
            />
            <p className="font-body mt-5 max-w-md text-sm leading-relaxed text-[color:var(--paper-1)]">
              The Pepe staff of the Toad Payroll Department processes transfers
              around the clock so TOAD envelopes keep leaving the building.
              The chairman thanks you for your service.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={X_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono-ledger inline-flex items-center gap-1.5 rounded-xl border border-[rgba(251,247,238,0.42)] px-4 py-2.5 text-[11px] uppercase tracking-[0.16em] text-[color:var(--paper-0)] transition-colors hover:border-[color:var(--stamp-500)] hover:text-[color:var(--stamp-500)]"
              >
                <XMark className="h-3.5 w-3.5" />
                X / Twitter
              </a>
              <a
                href={TELEGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono-ledger inline-flex items-center gap-1.5 rounded-xl border border-[rgba(251,247,238,0.42)] px-4 py-2.5 text-[11px] uppercase tracking-[0.16em] text-[color:var(--paper-0)] transition-colors hover:border-[color:var(--stamp-500)] hover:text-[color:var(--stamp-500)]"
              >
                <TelegramMark className="h-4 w-4" />
                Telegram
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="footer-badge-strip">
              {BADGES.map((b) => (
                <img
                  key={b}
                  src={`/fronk/props/${b}.webp`}
                  alt=""
                  className="h-14 w-14 object-contain transition-transform duration-300 hover:-rotate-6 hover:scale-110"
                  loading="lazy"
                />
              ))}
            </div>
          </div>

          <div className="paper-grain relative self-start overflow-hidden rounded-2xl bg-[color:var(--paper-0)] p-6 shadow-[var(--paper-lift)] sm:p-8">
            <div className="relative z-10">
              <span className="font-mono-ledger text-[11px] uppercase tracking-[0.2em] text-[color:var(--ink-700)]">
                Official mint address
              </span>
              <div className="mt-3 flex gap-2">
                <Input
                  readOnly
                  value={MINT}
                  className="font-mono-ledger h-11 border-[color:var(--ink-800)] bg-[color:var(--paper-1)] text-xs text-[color:var(--ink-900)]"
                  data-testid="mint-address-input"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  onClick={copyMint}
                  className="h-11 shrink-0 rounded-xl bg-[color:var(--ink-700)] px-4 text-[color:var(--paper-0)] transition-colors hover:bg-[color:var(--ink-800)]"
                  data-testid="mint-address-copy-button"
                  aria-label="Copy mint address"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <Button
                asChild
                size="lg"
                className="mt-4 w-full rounded-xl bg-[color:var(--stamp-600)] font-mono-ledger text-sm uppercase tracking-[0.12em] text-[color:var(--paper-0)] transition-colors hover:bg-[color:var(--stamp-500)]"
              >
                <a
                  href={STONKFUN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="buy-stonkfun-link"
                >
                  Buy $FRONK on StonkFun
                  <ArrowUpRight className="ml-1 h-4 w-4" />
                </a>
              </Button>
              <p className="font-mono-ledger mt-4 text-[10px] uppercase tracking-[0.16em] text-[color:var(--ink-700)]">
                Ticker $FRONK. Paired against TOAD on StonkFun.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-14 border-t border-[rgba(251,247,238,0.18)] pt-8">
          <p className="font-body max-w-3xl text-xs leading-relaxed text-[rgba(251,247,238,0.66)]">
            FRONK is a memecoin about a frog with a corner office. Fee
            mechanics are set by the StonkFun contract and described on the
            official token page. Numbers can change with the contract. Nothing
            on this page is financial advice. The chairman holds his phone
            upside down.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[rgba(251,247,238,0.55)]">
              The Toad Payroll Department. All shifts reserved.
            </p>
            <p className="font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[rgba(251,247,238,0.55)]">
              Fees in. TOAD out.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
